import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useAuthStore } from '../../../store/authStore'
import { supabase } from '../../../lib/supabase'

const PAGE_SIZE = 20

export const useWallPosts = () => {
  const household = useAuthStore((s) => s.household)
  const qc        = useQueryClient()
  const key       = ['wall', household?.id]

  const query = useInfiniteQuery({
    queryKey:         key,
    enabled:          !!household?.id,
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.cursor ?? null,
    queryFn: async ({ pageParam }) => {
      let q = supabase
        .from('wall_posts')
        .select(`id, body, photo_url, created_at, updated_at,
          profiles:author_id(id, display_name, member_color),
          wall_reactions(id, user_id, emoji)`)
        .eq('household_id', household.id)
        .order('created_at', { ascending: false })
        .limit(PAGE_SIZE)

      if (pageParam) q = q.lt('created_at', pageParam)

      const { data, error } = await q
      if (error) throw error
      const items = data ?? []
      return { items, cursor: items.length === PAGE_SIZE ? items[items.length - 1].created_at : null }
    },
  })

  // Realtime subscription
  useEffect(() => {
    if (!household?.id) return
    const channel = supabase
      .channel(`wall:${household.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'wall_posts', filter: `household_id=eq.${household.id}` },
        () => qc.invalidateQueries({ queryKey: key }),
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [household?.id])

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const createPost = useMutation({
    mutationFn: async ({ body, photo_url }) => {
      const { data: { session } } = await supabase.auth.getSession()
      const { error } = await supabase
        .from('wall_posts')
        .insert({ household_id: household.id, author_id: session.user.id, body, photo_url: photo_url || null })
      if (error) throw error
    },
    onSuccess: inv,
  })

  const deletePost = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('wall_posts').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: inv,
  })

  const toggleReaction = useMutation({
    mutationFn: async ({ postId, emoji }) => {
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session.user.id
      const existing = await supabase
        .from('wall_reactions')
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .eq('emoji', emoji)
        .maybeSingle()

      if (existing.data) {
        await supabase.from('wall_reactions').delete().eq('id', existing.data.id)
      } else {
        await supabase.from('wall_reactions').insert({ post_id: postId, user_id: userId, emoji })
      }
    },
    onSuccess: inv,
  })

  const allPosts = query.data?.pages.flatMap((p) => p.items) ?? []

  return {
    posts: allPosts,
    isLoading:     query.isLoading,
    fetchNextPage: query.fetchNextPage,
    hasNextPage:   !!query.data?.pages.at(-1)?.cursor,
    isFetchingNextPage: query.isFetchingNextPage,
    createPost,
    deletePost,
    toggleReaction,
  }
}
